import { Link } from "react-router-dom";
import { Megaphone } from "lucide-react";
import type { ArticleListItem } from "@/types/api";
import { CategoryPill } from "@/components/news/CategoryPill";
import { articleLeadImage, VideoCardBadge } from "@/components/news/VideoCardBadge";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { formatRelativeTime } from "@/lib/utils";

interface CompactArticleRowProps {
  article: ArticleListItem;
  index?: number;
}

export function CompactArticleRow({ article, index }: CompactArticleRowProps) {
  const image = articleLeadImage(article);

  return (
    <article className="flex min-h-[5.5rem] gap-3 border-b border-brand-outline-variant/40 py-3 last:border-0">
      {typeof index === "number" ? (
        <span className="w-8 shrink-0 font-display text-2xl font-bold leading-none text-brand-outline-variant/80">
          {String(index + 1).padStart(2, "0")}
        </span>
      ) : null}
      {image ? (
        <Link to={`/materia/${article.slug}`} className="relative h-16 w-16 shrink-0">
          <OptimizedImage
            src={image}
            alt={article.title}
            aspectRatio="1 / 1"
            wrapperClassName="h-16 w-16 rounded"
            sizes="64px"
            width={64}
            height={64}
          />
          {article.has_video ? <VideoCardBadge compact /> : null}
        </Link>
      ) : (
        <div className="h-16 w-16 shrink-0 rounded bg-brand-surface-container" aria-hidden />
      )}
      <div className="min-w-0 flex-1">
        <CategoryPill category={article.category} className="mb-1 px-2 py-0.5 text-[10px]" />
        <Link
          to={`/materia/${article.slug}`}
          className="line-clamp-2 font-display text-sm font-semibold leading-snug text-brand-ink hover:text-brand-primary"
        >
          {article.title}
        </Link>
        <p className="mt-1 text-xs uppercase tracking-wide text-brand-outline">
          {formatRelativeTime(article.publish_at || article.created_at)}
        </p>
      </div>
    </article>
  );
}

interface MostViewedPanelProps {
  articles: ArticleListItem[];
  title?: string;
}

export function MostViewedPanel({ articles, title = "Mais vistas" }: MostViewedPanelProps) {
  if (!articles.length) return null;

  return (
    <section className="min-h-[22rem] rounded border border-brand-outline-variant/50 bg-brand-surface-lowest p-4">
      <h2 className="mb-2 font-display text-lg font-bold text-brand-ink">{title}</h2>
      <div>
        {articles.slice(0, 4).map((article, index) => (
          <CompactArticleRow key={article.id} article={article} index={index} />
        ))}
      </div>
    </section>
  );
}

/** CTA abaixo de Mais vistas — leva ao formulário de Anuncie / Parcerias */
export function AdvertisePartnerCta() {
  return (
    <Link
      to="/anuncie"
      className="advertise-pulse group relative flex items-center gap-3 overflow-hidden rounded border border-brand-primary/30 bg-brand-chrome px-4 py-3.5 text-white shadow-sm transition hover:bg-brand-primary"
    >
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded bg-white/15"
        aria-hidden
      >
        <Megaphone className="h-5 w-5" />
      </span>
      <span className="min-w-0">
        <span className="block font-display text-sm font-bold uppercase tracking-wide">
          Anúncios e parcerias
        </span>
        <span className="mt-0.5 block text-xs text-white/85">
          Divulgue sua marca — preencha o formulário
        </span>
      </span>
    </Link>
  );
}

interface RelatedArticlesPanelProps {
  articles: ArticleListItem[];
  title?: string;
}

export function RelatedArticlesPanel({ articles, title = "Relacionadas" }: RelatedArticlesPanelProps) {
  if (!articles.length) return null;

  return (
    <section className="rounded border border-brand-outline-variant/50 bg-brand-surface-lowest p-4">
      <h2 className="mb-2 font-display text-lg font-bold text-brand-ink">{title}</h2>
      <div>
        {articles.slice(0, 5).map((article) => (
          <CompactArticleRow key={article.id} article={article} />
        ))}
      </div>
    </section>
  );
}
